import { RetailerAccount, User } from '@promoboxx/graphql-gateway-types'
import { useSelector } from 'react-redux'
import { Redirect } from 'react-router'

import routePath from '@src/lib/routePath'
import { userIsInternalSelector } from '@src/stores/app/selectors'

import downloadablesPaths from '../downloadables/downloadablesPaths'
import NotFound from '../routes/NotFound'

// The idea is we have one route that catches:
// - a feature name, so we can have some assurance of what we're dealing with.
// - everything else after that
//
// So if someone were to visit `/redirect/campaign/ascension/abcd1234`:
// - `featureName` would be `campaign`
export const redirectHandlerPaths = {
  root: routePath('/redirect/:featureName/*'),
}

// We can't really re-use any existing route paths here. Kinda by definition
// they are much different than these simple redirects.
// So we can define them here so that we have _some_ structure and are not 100%
// working with `any` types.
const featurePaths = {
  downloadables: routePath('/redirect/downloadables/:brandSlug/*'),
}

const RedirectHandler = () => {
  const originalSearch = window.location.search.substring(1)
  const originalHash = window.location.hash.substring(1)

  // Set either one of these. `r1Route` can technically go anywhere, it just
  // does `window.location.replace`, while `r2Route` is a react router redirect.
  let r1Route: string | undefined
  let r2Route: string | undefined

  const params = redirectHandlerPaths.root.useParams()
  const currentUser: User = useSelector((state) => state.auth.user)
  const isUserInternal = useSelector(userIsInternalSelector)

  if (
    !isUserInternal &&
    (currentUser.retailer_accounts == null ||
      currentUser.retailer_accounts.length === 0)
  ) {
    window.location.href = '/#/login/manageAccess'
  }

  switch (params.featureName) {
    case 'downloadables': {
      const match = featurePaths.downloadables.match(window.location.pathname)

      if (match) {
        const brandSlug = match.params.brandSlug
        const foundRetailerAccounts = findRetailerAccountsWithBrand(
          currentUser,
          brandSlug,
        )

        if (foundRetailerAccounts.length > 0) {
          let parent_id: string | undefined = undefined

          // The links generated in brand have the various folders nested in the
          // URL. This app only expects one (the last one).
          const folderPath = match.params[0]
          if (folderPath) {
            const folderParts = folderPath.split('/')
            parent_id = folderParts[folderParts.length - 1]
          }

          if (!parent_id) {
            parent_id = 'root'
          }

          r2Route = buildUrl(
            downloadablesPaths.index.build({
              retailerAccountSlug: foundRetailerAccounts[0]?.slug || '',
              brandSlug,
              parent_id,
            }),
            [originalSearch],
            originalHash,
          )
        }
      }
    }
  }

  if (r2Route) {
    return <Redirect to={r2Route} />
  }

  if (r1Route) {
    window.location.replace(r1Route)
    return null
  }

  return <NotFound />
}

function buildUrl(
  path: string,
  search: (string | null | undefined)[] = [],
  hash: string = '',
) {
  const searchJoined = search.filter(Boolean).join('&')

  const finalSearch = searchJoined ? `?${searchJoined}` : ''
  const finalHash = hash ? `#${hash}` : ''

  return `${path}${finalSearch}${finalHash}`
}

function findRetailerAccountsWithBrand(user: User, brandSlug: string) {
  const foundRetailerAccounts: RetailerAccount[] = []

  for (const retailerAccount of user.retailer_accounts || []) {
    for (const retailer of retailerAccount.retailers || []) {
      if (retailer?.brand?.slug === brandSlug) {
        foundRetailerAccounts.push(retailerAccount)
      }
    }
  }

  return foundRetailerAccounts
}

export default RedirectHandler
